import React, { useState } from "react";

// ── Default payloads sent to API ─────────────────────────────────────────────
const VEHICLE_API_DEFAULTS = {
  transporter_id: 1,
  registration_number: new Date().getTime().toString().padEnd(20, "0").slice(0, 20),
  model: "Default Model " + new Date().toISOString(),
  manufacturer: "Default Manufacturer " + new Date().toISOString(),
  year: 2000,
  capacity_kg: 30,
  is_available: true,
  fuel_type: "diesel " + new Date().toISOString(),
  fitness_expiry: new Date().toISOString().slice(0, 10),
  insurance_expiry: new Date().toISOString().slice(0, 10),
  color: "white " + new Date().toISOString()  ,
  vehicle_type: "Default " + new Date().toISOString(),
};

const DRIVER_API_DEFAULTS = {
  user_id: 1,
  transporter_id: 1,
  name: "Default Name " + new Date().toISOString(),
  license_number: "Default License Number " + new Date().toISOString(),
  license_expiry: new Date().toISOString().slice(0, 10),
  current_vehicle_id: null,
  status: "available",
  aadhar: null,
  address: null,
  city: null,
  state: null,
  upi_id: null,
  bank_account_name: null,
  bank_account_number: null,
  bank_ifsc: null,
  bank_name: null,
};

const API_URL = process.env.REACT_APP_API_URL ?? "";

// ── Shared style helpers ─────────────────────────────────────────────────────
const labelClass =
  "block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5";
const inputClass =
  "border border-slate-200 rounded-lg px-4 py-2.5 text-sm w-full bg-white " +
  "focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition placeholder:text-slate-300";
const cardClass =
  "bg-white border border-slate-100 rounded-2xl shadow-sm p-6 mb-6";

// ── Vehicle Section ───────────────────────────────────────────────────────────
const VehicleSection: React.FC = () => {
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [vehicles, setVehicles] = useState<{ vehicle_number: string; id: number }[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleNumber.trim()) {
      alert("Vehicle number is required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...VEHICLE_API_DEFAULTS,
        vehicle_number: vehicleNumber.trim(),
      };
      const resp = await fetch(`${API_URL}/auth/vehicles`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        let msg = "Failed to add vehicle.";
        try {
          const errBody = await resp.json();
          if (errBody?.error) msg = errBody.error;
        } catch {}
        throw new Error(msg);
      }
      const data = await resp.json();
      if (!data?.id) throw new Error("No vehicle id from backend.");
      setVehicles((prev) => [...prev, { vehicle_number: vehicleNumber.trim(), id: data.id }]);
      setVehicleNumber("");
    } catch (err: any) {
      alert(err?.message || "Could not save vehicle.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cardClass}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center text-white text-lg">
          🚛
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-800 leading-tight">Add Vehicle</h3>
          <p className="text-xs text-slate-400">Register a vehicle to the system</p>
        </div>
      </div>

      {/* Saved vehicles list */}
      {vehicles.length > 0 && (
        <div className="mb-5 rounded-xl bg-indigo-50 border border-indigo-100 overflow-hidden">
          <div className="px-4 py-2 bg-indigo-100 text-xs font-semibold uppercase tracking-widest text-indigo-600">
            Added Vehicles ({vehicles.length})
          </div>
          <ul className="divide-y divide-indigo-100">
            {vehicles.map((v, i) => (
              <li key={i} className="px-4 py-2.5 flex items-center justify-between">
                <span className="font-semibold text-indigo-700 text-sm">
                  {v.vehicle_number}
                </span>
                <span className="text-xs text-slate-400">ID: {v.id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Form — single field */}
      <form onSubmit={handleSubmit}>
        <div>
          <label className={labelClass}>Vehicle Number *</label>
          <input
            className={inputClass}
            placeholder="e.g. VH-001"
            value={vehicleNumber}
            onChange={(e) => {
              // Convert spaces to dashes
              setVehicleNumber(e.target.value.replace(/ /g, "-"));
            }}
            required
          />
 
        </div>
        <button
          type="submit"
          disabled={loading}
          className="mt-5 w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition disabled:opacity-60 text-sm tracking-wide"
        >
          {loading ? "Saving…" : "+ Add Vehicle"}
        </button>
      </form>
    </div>
  );
};

// ── Driver Section ────────────────────────────────────────────────────────────
const DriverSection: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [drivers, setDrivers] = useState<{ phone: string; id: number }[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) {
      alert("Driver phone number is required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...DRIVER_API_DEFAULTS,
        phone: phone.trim(),
      };
      const resp = await fetch(`${API_URL}/auth/drivers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        let msg = "Failed to create driver.";
        try {
          const e = await resp.json();
          if (e?.error) msg = e.error;
        } catch {}
        throw new Error(msg);
      }
      const data = await resp.json();
      if (!data?.id) throw new Error("No driver id from backend.");
      setDrivers((prev) => [...prev, { phone: phone.trim(), id: data.id }]);
      setPhone("");
    } catch (err: any) {
      alert(err?.message || "Could not save driver.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-emerald-600 flex items-center justify-center text-white text-lg">
          👤
        </div>
        <div>
          <h3 className="font-bold text-lg text-slate-800 leading-tight">Add Driver</h3>
          <p className="text-xs text-slate-400">Register a driver to the system</p>
        </div>
      </div>

      {/* Saved drivers list */}
      {drivers.length > 0 && (
        <div className="mb-5 rounded-xl bg-emerald-50 border border-emerald-100 overflow-hidden">
          <div className="px-4 py-2 bg-emerald-100 text-xs font-semibold uppercase tracking-widest text-emerald-700">
            Added Drivers ({drivers.length})
          </div>
          <ul className="divide-y divide-emerald-100">
            {drivers.map((d, i) => (
              <li key={i} className="px-4 py-2.5 flex items-center justify-between">
                <span className="font-semibold text-emerald-700 text-sm">{d.phone}</span>
                <span className="text-xs text-slate-400">ID: {d.id}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Form — single field */}
      <form onSubmit={handleSubmit}>
        <div>
          <label className={labelClass}>Driver Phone Number *</label>
          <input
            className={inputClass}
            placeholder="e.g. 9876543210"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="mt-5 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition disabled:opacity-60 text-sm tracking-wide"
        >
          {loading ? "Saving…" : "+ Add Driver"}
        </button>
      </form>
    </div>
  );
};

// ── Main Export ───────────────────────────────────────────────────────────────
const AddVehicleDriver: React.FC = () => {
  return (
    <div className="w-full max-w-3xl mx-auto py-8 px-4 md:px-8 bg-slate-50 min-h-screen">
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Fleet Management</h2>
        <p className="text-sm text-slate-400 mt-1">Add vehicles and drivers independently</p>
      </div>
      <VehicleSection />
      <DriverSection />
    </div>
  );
};

export default AddVehicleDriver;